import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLazyGetChartImageQuery } from '@/store/api/BasicChart/chartImagesApi';
import Modal from '@/utils/modal.utils';
import Loader from '@/components/Loader';

const OTHER_CHARTS = [
  {
    id: 'D12',
    name: 'Dwadashamsha',
    title: 'DWADASHAMSHA CHART (SOUTH INDIAN & NORTH INDIAN)',
    subTitle: 'DWADASHAMSHA CHART',
  },
  {
    id: 'D16',
    name: 'Shodashamsha',
    title: 'SHODASHAMSHA CHART (SOUTH INDIAN & NORTH INDIAN)',
    subTitle: 'SHODASHAMSHA CHART',
  },
  {
    id: 'D24',
    name: 'Chaturvimshamsha',
    title: 'CHATURVIMSHAMSHA CHART (SOUTH INDIAN & NORTH INDIAN)',
    subTitle: 'CHATURVIMSHAMSHA CHART',
  },
  {
    id: 'D60',
    name: 'Shashtiamsha',
    title: 'SHASHTIAMSHA CHART (SOUTH INDIAN & NORTH INDIAN)',
    subTitle: 'SHASHTIAMSHA CHART',
  },
] as const;

export default function OtherCharts(): JSX.Element {
  const [params] = useSearchParams();
  const userId = params.get('userId');

  const [loading, setLoading] = useState(false);
  const [charts, setCharts] = useState<Record<string, any>>({});

  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState<string[]>([]);
  const [selectedTitle, setSelectedTitle] = useState('');

  const [fetchChartImage] = useLazyGetChartImageQuery();

  useEffect(() => {
    async function fetchAllCharts() {
      if (!userId) return;

      setLoading(true);
      try {
        const payload = {
          userId: String(userId),
          imageType: 'png' as const,
        };

        for (const chart of OTHER_CHARTS) {
          try {
            const south = await fetchChartImage({
              ...payload,
              chartId: chart.id,
              chartType: 'south',
            }).unwrap();

            const north = await fetchChartImage({
              ...payload,
              chartId: chart.id,
              chartType: 'north',
            }).unwrap();

            setCharts((prev) => ({
              ...prev,
              [`${chart.id}-South`]: south,
              [`${chart.id}-North`]: north,
            }));
          } catch (err) {
            console.error(`Error fetching chart ${chart.id}:`, err);
          }
        }
      } catch (error) {
        console.error('Failed to load other charts:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchAllCharts();
  }, [userId, fetchChartImage]);

  const openModal = (clickedKey: string, title: string) => {
    const [chartId] = clickedKey.split('-');

    const south = charts[`${chartId}-South`]?.chartUrl;
    const north = charts[`${chartId}-North`]?.chartUrl;

    const images = [south, north].filter(Boolean) as string[];

    setModalImages(images);
    setSelectedTitle(title.replace(/(South|North)/, 'South Indian & North Indian'));
    setModalOpen(true);
  };

  if (loading && Object.keys(charts).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <Loader />
        <p className="text-gray-500 font-spectral tracking-wide animate-pulse">
          Loading Other Charts...
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl">
      <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-4">
        {OTHER_CHARTS.map((chart) => {
          const southUrl = charts[`${chart.id}-South`]?.chartUrl;
          const northUrl = charts[`${chart.id}-North`]?.chartUrl;

          // Only render if we have loaded at least one image style
          if (!southUrl && !northUrl) return null;

          return (
            <div
              key={chart.id}
              className="col-span-2 border border-[#2DAAFD] p-6 rounded-xl w-full"
            >
              <h2 className="font-22 font-semibold font-spectral mb-4 bg-clip-text text-transparent bg-gradient-to-r from-[#082261] to-[#34065c] text-center">
                {chart.title}
              </h2>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {southUrl && (
                  <div>
                    <p className="font-semibold text-center font-spectral uppercase text-sm mb-2">
                      {chart.subTitle}
                    </p>
                    <img
                      src={southUrl}
                      alt={`${chart.name} South`}
                      className="w-full rounded-md border p-[9px] border-[#2DAAFD] cursor-pointer bg-white"
                      onClick={() => openModal(`${chart.id}-South`, `${chart.name} Chart (South)`)}
                    />
                  </div>
                )}

                {northUrl && (
                  <div>
                    <p className="font-semibold text-center font-spectral uppercase text-sm mb-2">
                      {chart.subTitle}
                    </p>
                    <img
                      src={northUrl}
                      alt={`${chart.name} North`}
                      className="w-full rounded-md border border-[#2DAAFD] cursor-pointer bg-white"
                      onClick={() => openModal(`${chart.id}-North`, `${chart.name} Chart (North)`)}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedTitle}
        widthClass="max-w-4xl"
      >
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 w-full">
          {modalImages.map((img, i) => (
            <div key={i} className="flex-1 w-full flex justify-center items-center">
              <img
                src={img}
                alt="Expanded Chart"
                className="max-h-[60vh] w-auto max-w-full rounded-md object-contain border border-[#2DAAFD] p-1 bg-white"
              />
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
